<template>
  <div class="relative min-h-screen w-full bg-gradient-to-br from-gray-100 to-blue-50">
    <div class="container mx-auto px-4 sm:px-8 py-8">

      <!-- Breadcrumb -->
      <nav class="text-sm text-gray-500 mb-5 flex items-center flex-wrap">
        <router-link to="/dashboard" class="hover:text-blue-700 font-medium">Dashboard</router-link>
        <span class="mx-1.5 text-gray-300">/</span>
        <router-link to="/partnerships" class="hover:text-blue-700 font-medium">Partnerships</router-link>
        <span class="mx-1.5 text-gray-300">/</span>
        <router-link to="/current-partners" class="hover:text-blue-700 font-medium">Current Partners</router-link>
        <span class="mx-1.5 text-gray-300">/</span>
        <span class="text-gray-800 font-semibold">{{ item && item.name ? item.name : 'View Partner' }}</span>
      </nav>

      <!-- Page Header -->
      <div class="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
        <div>
          <p class="text-xs font-bold uppercase tracking-widest text-blue-600 m-0 mb-1">Partnerships</p>
          <h1 class="text-3xl font-extrabold text-gray-900 m-0">
            {{ item && item.name ? item.name : 'Partner Details' }}
          </h1>
          <p class="text-gray-500 mt-1 mb-0 text-sm">
            Review partnership details, contacts, documents, and MoU effectiveness.
          </p>
        </div>
        <div class="flex items-center gap-3 flex-wrap">
          <router-link to="/current-partners" class="cp-btn-ghost">
            <font-awesome-icon :icon="['fas', 'arrow-left']" />
            <span>Back to List</span>
          </router-link>
          <button v-if="item && hasPermission('current-partners-edit')" type="button" class="cp-btn-primary"
            @click="openEdit">
            <icon-pen class="w-4 h-4"></icon-pen>
            <span>Edit Partner</span>
          </button>
        </div>
      </div>

      <!-- Loading -->
      <div v-if="isLoading"
        class="bg-white rounded-2xl border border-gray-200 shadow-sm flex justify-center items-center py-24">
        <base-page-spinner type="spin" class-list="h-20"></base-page-spinner>
      </div>

      <!-- Error -->
      <div v-else-if="error"
        class="bg-white rounded-2xl border border-gray-200 shadow-sm flex flex-col justify-center items-center py-20 px-6">
        <div class="cp-error-icon">
          <font-awesome-icon :icon="['fas', 'exclamation-circle']" />
        </div>
        <p class="text-lg font-bold text-gray-800 mb-1 mt-4">Unable to load partner</p>
        <p class="text-gray-500 text-sm mb-6">{{ error }}</p>
        <router-link to="/current-partners" class="cp-btn-primary">Back to Current Partners</router-link>
      </div>

      <template v-else-if="item">
        <!-- Hero summary -->
        <section class="cp-hero mb-6">
          <div class="cp-hero__logo">
            <img v-if="item.logo" :src="getSrc(item.logo)" alt="" />
            <font-awesome-icon v-else :icon="['fas', 'building']" class="cp-hero__logo-fallback" />
          </div>
          <div class="cp-hero__body">
            <div class="flex flex-wrap items-center gap-2 mb-2">
              <h2 class="text-xl font-extrabold text-gray-900 m-0">{{ item.name }}</h2>
              <span v-if="partnerTier" :class="partnerTierBadgeClass(partnerTier)">{{ partnerTier }}</span>
              <span v-if="isShowOnLandingPage" class="cp-meta-chip cp-meta-chip--success">Landing visible</span>
              <span v-else class="cp-meta-chip cp-meta-chip--muted">Landing hidden</span>
            </div>
            <div class="flex flex-wrap gap-2">
              <span v-if="item.entity" class="cp-meta-chip">
                <font-awesome-icon :icon="['fas', 'sitemap']" class="mr-1" />
                {{ getEntityName(item.entity) }}
              </span>
              <span v-if="industryName" class="cp-meta-chip">
                <font-awesome-icon :icon="['fas', 'industry']" class="mr-1" />
                {{ industryName }}
              </span>
              <span v-if="item.type_of_collaboration" class="cp-meta-chip">
                <font-awesome-icon :icon="['fas', 'handshake']" class="mr-1" />
                {{ item.type_of_collaboration }}
              </span>
              <span v-if="item.location" class="cp-meta-chip">
                <font-awesome-icon :icon="['fas', 'map-marker-alt']" class="mr-1" />
                {{ item.location }}
              </span>
              <span v-if="item.updated_at" class="cp-meta-chip cp-meta-chip--muted">
                Updated {{ getFormattedDate(item.updated_at) }}
              </span>
            </div>
            <p v-if="classificationsLabel" class="text-sm text-gray-500 mt-3 mb-0">
              <span class="font-semibold text-gray-600">Classifications:</span>
              {{ classificationsLabel }}
            </p>
          </div>
        </section>

        <!-- Quick stats -->
        <div class="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4 mb-6">
          <div class="cp-stat-card cp-stat-card--contacts">
            <div class="cp-stat-card__icon">
              <font-awesome-icon :icon="['fas', 'users']" />
            </div>
            <div class="cp-stat-card__body">
              <p class="cp-stat-card__value">{{ contactCount }}</p>
              <p class="cp-stat-card__label">Contacts</p>
            </div>
          </div>
          <div class="cp-stat-card cp-stat-card--jcm">
            <div class="cp-stat-card__icon">
              <font-awesome-icon :icon="['fas', 'user-tie']" />
            </div>
            <div class="cp-stat-card__body">
              <p class="cp-stat-card__value">{{ jointCommitteeCount }}</p>
              <p class="cp-stat-card__label">Joint Committee</p>
            </div>
          </div>
          <div class="cp-stat-card cp-stat-card--docs">
            <div class="cp-stat-card__icon">
              <font-awesome-icon :icon="['fas', 'paperclip']" />
            </div>
            <div class="cp-stat-card__body">
              <p class="cp-stat-card__value">{{ documentCount }}</p>
              <p class="cp-stat-card__label">Documents</p>
            </div>
          </div>
          <div class="cp-stat-card cp-stat-card--mou">
            <div class="cp-stat-card__icon">
              <font-awesome-icon :icon="['fas', 'clipboard-check']" />
            </div>
            <div class="cp-stat-card__body">
              <p class="cp-stat-card__value">{{ mouCount }}</p>
              <p class="cp-stat-card__label">MoU Cards</p>
            </div>
          </div>
          <div class="cp-stat-card cp-stat-card--alumni" role="button" tabindex="0" @click="goToSection('employment')"
            @keyup.enter="goToSection('employment')">
            <div class="cp-stat-card__icon">
              <font-awesome-icon :icon="['fas', 'graduation-cap']" />
            </div>
            <div class="cp-stat-card__body">
              <p class="cp-stat-card__value">{{ alumniCountDisplay }}</p>
              <p class="cp-stat-card__label">Employed</p>
            </div>
          </div>
        </div>

        <!-- Tabs -->
        <div class="cp-tabs-bar mb-5">
          <button v-for="tab in sections" :key="tab.value" type="button" class="cp-tab"
            :class="{ 'cp-tab--active': activeSection === tab.value }" @click="activeSection = tab.value">
            <font-awesome-icon :icon="tab.icon" class="cp-tab__icon" />
            <span>{{ tab.label }}</span>
            <span v-if="tab.badge" class="cp-tab__badge">{{ tab.badge }}</span>
          </button>
        </div>

        <!-- Partner Record -->
        <section v-if="activeSection === 'record'" class="cp-panel">
          <div class="cp-panel__header">
            <div>
              <h2 class="cp-panel__title">Partner Record</h2>
              <p class="cp-panel__subtitle">Full partnership profile and related records</p>
            </div>
          </div>
          <div class="cp-panel__body">
            <current-partners-view :item="item" :standalone="true" />
          </div>
        </section>

        <!-- Employment Insights -->
        <section v-if="activeSection === 'employment'" class="cp-panel">
          <div class="cp-panel__header">
            <div>
              <h2 class="cp-panel__title">Employment Insights</h2>
              <p class="cp-panel__subtitle">Alumni employed at this partner by entity, campus, academic year, and
                program</p>
            </div>
            <button v-if="hasPermission('current-partners-edit')" type="button" class="cp-btn-primary"
              @click="openLinkEmployersModal">
              <font-awesome-icon :icon="['fas', 'link']" />
              <span>Link Employers</span>
            </button>
          </div>
          <div class="cp-panel__body">
            <partner-employment-charts ref="employmentCharts" :partner-token="item.token" />
          </div>
        </section>
      </template>
    </div>

    <transition name="fade" mode="out-in">
      <base-full-modal v-if="editModal && item" :title="'Edit Current Partner - ' + item.name" @close="closeEdit">
        <edit class="text-left" :entities="filteredEntities" :selected-item="item" @updated="onUpdated"
          @close="closeEdit">
        </edit>
      </base-full-modal>
    </transition>

    <link-employers-modal v-if="item && linkEmployersModal" :visible="linkEmployersModal" :partner-token="item.token"
      :partner-name="item.name" @close="closeLinkEmployersModal" @linked="onEmployersLinked" />
  </div>
</template>

<script>
import gql from "graphql-tag";
import CurrentPartnersView from "./view.vue";
import Edit from "./edit.vue";
import IconPen from "../../components/icons/IconPen.vue";
import PartnerEmploymentCharts from "./charts/PartnerEmploymentCharts.vue";
import LinkEmployersModal from "./components/LinkEmployersModal.vue";
import { formattedDate } from "../../helpers/utils";
import { evaluatePartnerTier, getTierBadgeClass } from "../../helpers/partnerTier";

export default {
  name: "current-partners-view-page",
  components: {
    CurrentPartnersView,
    Edit,
    IconPen,
    PartnerEmploymentCharts,
    LinkEmployersModal,
  },
  props: {
    token: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      item: null,
      isLoading: true,
      error: null,
      editModal: false,
      linkEmployersModal: false,
      entities: [],
      activeSection: "record",
      sections: [
        { value: "record", label: "Partner Record", icon: ["fas", "folder-open"], badge: "" },
        { value: "employment", label: "Employment Insights", icon: ["fas", "chart-bar"], badge: "" },
      ],
      alumniEmployedCount: null,
      employmentStatsLoading: false,
    };
  },
  computed: {
    role() {
      return this.$store.getters.role;
    },
    filteredEntities() {
      var entities = this.entities && this.entities.data ? this.entities.data : [];
      var hasIAT = false;
      for (var i = 0; i < entities.length; i++) {
        if (entities[i].entity === "IAT") {
          hasIAT = true;
          break;
        }
      }
      if (!hasIAT) {
        return entities.concat([{ entity: "IAT" }]);
      }
      return entities;
    },
    partnerTier() {
      return evaluatePartnerTier(this.item);
    },
    industryName() {
      if (this.item && this.item.industry_relation && this.item.industry_relation.name) {
        return this.item.industry_relation.name;
      }
      return "";
    },
    classificationsLabel() {
      if (!this.item || !this.item.classifications || this.item.classifications.length === 0) {
        return "";
      }
      return this.item.classifications.join(", ");
    },
    isShowOnLandingPage() {
      if (!this.item) {
        return false;
      }
      if (
        this.item.show_on_landing_page === false ||
        this.item.show_on_landing_page === 0 ||
        this.item.show_on_landing_page === "0"
      ) {
        return false;
      }
      return !!this.item.show_on_landing_page;
    },
    contactCount() {
      return this.item && this.item.contact_person ? this.item.contact_person.length : 0;
    },
    jointCommitteeCount() {
      return this.item && this.item.joint_committee_members
        ? this.item.joint_committee_members.length
        : 0;
    },
    documentCount() {
      if (!this.item || !this.item.attachments) {
        return 0;
      }
      var count = 0;
      for (var i = 0; i < this.item.attachments.length; i++) {
        if (this.item.attachments[i].uploaded_from === "Upload Documents") {
          count++;
        }
      }
      return count;
    },
    mouCount() {
      return this.item && this.item.mou_effectiveness_cards
        ? this.item.mou_effectiveness_cards.length
        : 0;
    },
    alumniCountDisplay() {
      if (this.employmentStatsLoading || this.alumniEmployedCount === null) {
        return "—";
      }
      return this.alumniEmployedCount;
    },
  },
  apollo: {
    entities() {
      return {
        query: gql`
          query {
            entities {
              data {
                entity
              }
            }
          }
        `,
      };
    },
  },
  created() {
    this.fetchPartner();
  },
  watch: {
    token() {
      this.fetchPartner();
    },
  },
  methods: {
    hasPermission(crudName) {
      if (this.role == "superadmin") {
        return true;
      }
      var permissions = this.$store.getters.permissions;
      if (!permissions) {
        return false;
      }
      var permissionsArray = permissions.split(",");
      return permissionsArray.indexOf(crudName) !== -1;
    },
    getSrc(url) {
      return process.env.VUE_APP_PUBLIC_URL + url;
    },
    getEntityName(entity) {
      if (!entity) {
        return "";
      }
      return String(entity).replace(/ATHS/g, "ATS");
    },
    getFormattedDate(date) {
      return formattedDate(date);
    },
    partnerTierBadgeClass(tier) {
      return getTierBadgeClass(tier);
    },
    openEdit() {
      this.editModal = true;
    },
    closeEdit() {
      this.editModal = false;
    },
    openLinkEmployersModal() {
      this.linkEmployersModal = true;
    },
    closeLinkEmployersModal() {
      this.linkEmployersModal = false;
    },
    onEmployersLinked() {
      if (this.$refs.employmentCharts && this.$refs.employmentCharts.fetchStats) {
        this.$refs.employmentCharts.fetchStats();
      }
      this.fetchEmploymentStats();
    },
    goToSection(section) {
      this.activeSection = section;
    },
    updateTabBadges() {
      var count = this.alumniEmployedCount;
      for (var i = 0; i < this.sections.length; i++) {
        if (this.sections[i].value === "employment") {
          this.sections[i].badge =
            count !== null && count > 0 ? String(count) : "";
        }
      }
    },
    async fetchEmploymentStats() {
      if (!this.item || !this.item.token) {
        return;
      }

      this.employmentStatsLoading = true;

      try {
        var result = await this.$apollo.query({
          query: gql`
            query GetCurrentPartnerEmploymentStats($token: String!) {
              getCurrentPartnerEmploymentStats(token: $token) {
                error
                message
                data {
                  total_employed
                }
              }
            }
          `,
          variables: { token: this.item.token },
          fetchPolicy: "network-only",
        });

        var payload =
          result.data && result.data.getCurrentPartnerEmploymentStats
            ? result.data.getCurrentPartnerEmploymentStats
            : null;

        if (payload && !payload.error && payload.data) {
          this.alumniEmployedCount = payload.data.total_employed || 0;
        } else {
          this.alumniEmployedCount = 0;
        }
      } catch (err) {
        this.alumniEmployedCount = 0;
      } finally {
        this.employmentStatsLoading = false;
        this.updateTabBadges();
      }
    },
    onUpdated(msg) {
      this.editModal = false;
      this.fetchPartner();
      if (this.$swal && msg) {
        var Toast = this.$swal.mixin({
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 1000,
        });
        Toast.fire({
          icon: "success",
          title: msg,
        });
      }
    },
    async fetchPartner() {
      this.isLoading = true;
      this.error = null;
      this.item = null;

      try {
        var result = await this.$apollo.query({
          query: gql`
            query GetCurrentPartnerByToken($token: String!) {
              getCurrentPartnerByToken(token: $token) {
                error
                message
                data {
                  id
                  token
                  name
                  logo
                  entity
                  location
                  industry
                  industry_relation {
                    id
                    name
                  }
                  type_of_collaboration
                  classifications
                  show_on_landing_page
                  parent_partner_id
                  parent_partner {
                    id
                    name
                    token
                    is_pipeline
                  }
                  subsidiary_partners {
                    id
                    token
                    name
                    logo
                    entity
                    industry
                    industry_relation {
                      id
                      name
                    }
                    type_of_collaboration
                    classifications
                    is_pipeline
                  }
                  contact_person {
                    id
                    name
                    phone
                    email
                    designation
                  }
                  joint_committee_members {
                    id
                    user_id
                    name
                    title
                    entity
                    display_entity
                    mobile
                    email
                    is_internal
                    user {
                      id
                      name
                      email
                      phone_number
                      position
                      entity
                    }
                  }
                  account_managers {
                    id
                    user_id
                    name
                    title
                    entity
                    display_entity
                    mobile
                    email
                    user {
                      id
                      name
                      email
                      phone_number
                      position
                      entity
                    }
                  }
                  attachments {
                    id
                    file_name
                    original_filename
                    file_extension
                    path
                    uploaded_from
                  }
                  mou_effectiveness_cards {
                    id
                    first_party_labels
                    second_party
                    created_at
                    updated_at
                  }
                  updated_at
                  is_signed
                  approved_at
                  is_bot_approval
                  is_md_approval
                  is_line_manager_approval
                }
              }
            }
          `,
          variables: { token: this.token },
          fetchPolicy: "network-only",
        });

        var payload = result.data && result.data.getCurrentPartnerByToken
          ? result.data.getCurrentPartnerByToken
          : null;
        if (!payload) {
          this.error = "Failed to load partner";
        } else if (payload.error) {
          this.error = payload.message || "Failed to load partner";
        } else if (payload.data && payload.data.length > 0) {
          this.item = payload.data[0];
          this.activeSection = "record";
          this.alumniEmployedCount = null;
          this.fetchEmploymentStats();
        } else {
          this.error = "Partner not found";
        }
      } catch (err) {
        this.error = (err && err.message) ? err.message : "Failed to load partner";
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.cp-btn-primary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.375rem;
  padding: 0.5rem 1.125rem;
  font-size: 0.8125rem;
  font-weight: 600;
  color: #fff;
  background: linear-gradient(180deg, #2563eb 0%, #1d4ed8 100%);
  border: none;
  border-radius: 0.5rem;
  cursor: pointer;
  text-decoration: none;
  transition: box-shadow 0.2s;
}

.cp-btn-primary:hover {
  box-shadow: 0 4px 12px -2px rgba(37, 99, 235, 0.45);
  color: #fff;
}

.cp-btn-ghost {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.5rem 0.875rem;
  font-size: 0.8125rem;
  font-weight: 600;
  color: #374151;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  cursor: pointer;
  text-decoration: none;
  transition: all 0.2s;
}

.cp-btn-ghost:hover {
  background: #f9fafb;
  border-color: #d1d5db;
  color: #111827;
}

.cp-error-icon {
  width: 4rem;
  height: 4rem;
  border-radius: 1rem;
  background: #fee2e2;
  color: #dc2626;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.75rem;
}

.cp-hero {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  padding: 1.5rem;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 1rem;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}

@media (min-width: 640px) {
  .cp-hero {
    flex-direction: row;
    align-items: center;
    padding: 1.75rem 2rem;
  }
}

.cp-hero__logo {
  flex-shrink: 0;
  width: 5.5rem;
  height: 5.5rem;
  border-radius: 0.875rem;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  padding: 0.5rem;
}

.cp-hero__logo img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.cp-hero__logo-fallback {
  font-size: 1.75rem;
  color: #94a3b8;
}

.cp-hero__body {
  min-width: 0;
  flex: 1;
}

.cp-meta-chip {
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.625rem;
  border-radius: 0.375rem;
  font-size: 0.75rem;
  font-weight: 600;
  background: #eff6ff;
  color: #1d4ed8;
  border: 1px solid #dbeafe;
}

.cp-meta-chip--success {
  background: #ecfdf5;
  color: #047857;
  border-color: #a7f3d0;
}

.cp-meta-chip--muted {
  background: #f8fafc;
  color: #64748b;
  border-color: #e2e8f0;
}

.cp-stat-card {
  display: flex;
  align-items: center;
  gap: 0.875rem;
  padding: 1.25rem 1rem;
  border-radius: 1rem;
  background: #fff;
  border: 1px solid #e5e7eb;
}

.cp-stat-card__icon {
  width: 2.75rem;
  height: 2.75rem;
  border-radius: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  flex-shrink: 0;
}

.cp-stat-card__body {
  min-width: 0;
}

.cp-stat-card__value {
  font-size: 1.5rem;
  font-weight: 800;
  line-height: 1.2;
  margin: 0;
  color: #1e293b;
}

.cp-stat-card__label {
  font-size: 0.8125rem;
  font-weight: 600;
  margin: 0.125rem 0 0;
  color: #475569;
}

.cp-stat-card--contacts .cp-stat-card__icon {
  background: #dbeafe;
  color: #2563eb;
}

.cp-stat-card--jcm .cp-stat-card__icon {
  background: #ede9fe;
  color: #7c3aed;
}

.cp-stat-card--docs .cp-stat-card__icon {
  background: #fef3c7;
  color: #d97706;
}

.cp-stat-card--mou .cp-stat-card__icon {
  background: #d1fae5;
  color: #059669;
}

.cp-stat-card--alumni {
  cursor: pointer;
  transition: box-shadow 0.2s, border-color 0.2s;
}

.cp-stat-card--alumni:hover {
  border-color: #bfdbfe;
  box-shadow: 0 4px 12px -4px rgba(37, 99, 235, 0.25);
}

.cp-stat-card--alumni .cp-stat-card__icon {
  background: #e0e7ff;
  color: #4338ca;
}

.cp-tabs-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  padding: 0.375rem;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 0.75rem;
}

.cp-tab {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 1rem;
  font-size: 0.8125rem;
  font-weight: 600;
  color: #64748b;
  background: transparent;
  border: none;
  border-radius: 0.5rem;
  cursor: pointer;
  transition: all 0.15s;
}

.cp-tab:hover {
  background: #f1f5f9;
  color: #334155;
}

.cp-tab--active {
  background: #2563eb;
  color: #fff;
}

.cp-tab--active:hover {
  background: #1d4ed8;
  color: #fff;
}

.cp-tab__icon {
  font-size: 0.875rem;
}

.cp-tab__badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.25rem;
  height: 1.25rem;
  padding: 0 0.35rem;
  font-size: 0.6875rem;
  font-weight: 700;
  border-radius: 9999px;
  background: rgba(255, 255, 255, 0.25);
  color: inherit;
}

.cp-tab:not(.cp-tab--active) .cp-tab__badge {
  background: #dbeafe;
  color: #1d4ed8;
}

.cp-panel {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 1rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  overflow: hidden;
}

.cp-panel__header {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.5rem 1.5rem 1rem;
  border-bottom: 1px solid #f1f5f9;
}

.cp-panel__title {
  font-size: 1.125rem;
  font-weight: 700;
  color: #111827;
  margin: 0;
}

.cp-panel__subtitle {
  font-size: 0.8125rem;
  color: #64748b;
  margin: 0.25rem 0 0;
}

.cp-panel__body {
  padding: 1rem 1.5rem 1.5rem;
}

.partner-tier-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.125rem 0.5rem;
  border-radius: 0.375rem;
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.02em;
}

.partner-tier-badge-strategic {
  background: #ede9fe;
  color: #6d28d9;
}

.partner-tier-badge-active {
  background: #dbeafe;
  color: #1d4ed8;
}

.partner-tier-badge-engaged {
  background: #fef3c7;
  color: #b45309;
}

.partner-tier-badge-shelved {
  background: #f1f5f9;
  color: #475569;
}
</style>
